import { inject, Injectable } from '@angular/core';
import { ApiDataAccess } from '@softline/core';
import {
  SOFTLINE_API_SUPPLIER_DTI_DEFAULT_PRODUCT,
  SOFTLINE_API_SUPPLIER_DTI_UNIT_MAPPINGS,
} from '../ai-table-extractor.softline-api';
import {
  DtiDefaultProductResponse,
  DtiUnitMappingResponse,
} from '../types/supplier-profile';

/** The supplier's DTI profile on the supplier-service; a 404 means "not configured". */
@Injectable()
export class SupplierProfileRepository {
  private readonly dataAccess = inject(ApiDataAccess);

  /** Null when the supplier has no default product (404); other failures propagate. */
  async loadDefaultProduct(supplierId: string): Promise<DtiDefaultProductResponse | null> {
    return this.orNotConfigured(
      this.dataAccess.get<DtiDefaultProductResponse | null>({
        path: SOFTLINE_API_SUPPLIER_DTI_DEFAULT_PRODUCT,
        pathParams: { supplierId },
      }),
      null
    );
  }

  /** Empty when the supplier has no unit mappings (404); other failures propagate. */
  async loadUnitMappings(supplierId: string): Promise<DtiUnitMappingResponse[]> {
    const mappings = await this.orNotConfigured(
      this.dataAccess.get<DtiUnitMappingResponse[] | null>({
        path: SOFTLINE_API_SUPPLIER_DTI_UNIT_MAPPINGS,
        pathParams: { supplierId },
      }),
      null
    );
    return mappings ?? [];
  }

  private async orNotConfigured<T>(request: Promise<T>, fallback: T): Promise<T> {
    try {
      return await request;
    } catch (e) {
      if ((e as { status?: number } | null)?.status === 404) return fallback;
      throw e;
    }
  }
}
